/**
 * ProcessesPendingBadge — badge licznika czekających decyzji przy linku
 * „Procesy" w sidebarze (F17 Etap 1.5).
 *
 * Liczba: `GET /api/processes/my-pending-decisions` → pole `total` (pełny
 * count, niezależny od limit — patrz route.ts). Wołamy z `limit:1` bo
 * potrzebny tylko `total`, nie wiersze.
 *
 * Klucz query pod prefiksem `['processes']` → auto-refresh za darmo: panele
 * decyzji / zgłoszenia / anulowania robią `invalidateQueries(['processes'])`.
 *
 * Render: `total<=0` / loading / error → null (silent-fail, jak
 * `CustomModulesSection`; dot. też 402 gdy moduł nieaktywny — choć badge
 * mountuje się tylko gdy link Procesów renderowany, więc moduł jest aktywny).
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/Badge';
import { fetchMyPendingDecisions } from '@/lib/processes/approvals-api';

export function ProcessesPendingBadge() {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ['processes', 'my-pending-decisions', 'badge'],
    queryFn: () => fetchMyPendingDecisions({ limit: 1 }),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  // Dev-only sygnał — silent-fail dla usera, ale przy trwałym błędzie (np. dryf
  // schematu Procesów na tenancie — brak tabeli `process_instances`, CLAUDE.md §6
  // Krok 6) dev dostaje wskazówkę. Lustro wzorca z guardu custom_link w Sidebar.
  if (import.meta.env.DEV && query.isError) {
    console.warn(
      '[ProcessesPendingBadge] nie udało się pobrać liczby czekających decyzji',
      query.error,
    );
  }

  const total = query.data?.total ?? 0;
  if (query.isLoading || query.isError || total <= 0) return null;

  const display = total > 99 ? '99+' : String(total);

  return (
    <Badge
      color="accent"
      className="ml-auto tabular-nums"
      data-testid="processes-pending-badge"
      aria-label={t('tenant.sidebar.pendingDecisionsBadge', { count: total })}
    >
      {display}
    </Badge>
  );
}
